import React from "react";
import { Helmet } from "react-helmet";
import { OG_IMAGE_CACHE_VERSION } from "../../helpers/constants";

/**
 * SEOHead - Dynamic SEO Meta Tags for Vacancy Landing Pages
 *
 * Generates Open Graph, Twitter Card, and standard meta tags
 * based on listing data for viral sharing on Facebook, SMS, etc.
 *
 * This component enhances the basic SEO already in VacancyLandingPage
 * with comprehensive meta tags for better social sharing.
 *
 * @param {Object} props
 * @param {Object} props.listingData - The listing data object
 * @param {string} props.listingData.headline - Listing headline/title
 * @param {string} props.listingData.description - Property description
 * @param {number} props.listingData.rentAmount - Monthly rent amount
 * @param {string[]} props.listingData.photos - Array of photo URLs
 * @param {string} props.listingData.address - Property address
 * @param {string} props.listingId - The listing ID from URL
 */
const SEOHead = ({ listingData, listingId }) => {
  if (!listingData) return null;

  // Extract data with fallbacks
  const headline =
    listingData.headline ||
    listingData.title ||
    `${listingData.bedrooms || ""} Bed, ${listingData.bathrooms || ""} Bath`;
  const description =
    listingData.description ||
    "Check out this listing on LandlordOps. Verified Landlord.";
  const rentAmount = listingData.rentAmount || listingData.monthlyRent || 0;
  const rentFormatted = rentAmount
    ? `$${rentAmount.toLocaleString()}/mo`
    : "Contact for pricing";

  // Get cover photo (explicit metaImageUrl first, then first photo in array, then fallback)
  const coverPhoto =
    listingData.metaImageUrl ||
    listingData.publicMetadata?.metaImageUrl ||
    listingData.photos?.[0] ||
    listingData.photo ||
    listingData.coverImage ||
    `${window.location.origin}/meta.png?v=${OG_IMAGE_CACHE_VERSION}`; // Default OG; bump OG_IMAGE_CACHE_VERSION in constants when asset changes

  // Build full title
  const fullTitle = `${headline} - ${rentFormatted} | LandlordOps`;

  // Build full description (truncate to 155 chars for optimal SEO)
  const fullDescription =
    description.length > 155
      ? description.substring(0, 152) + "..."
      : description;

  // Build canonical URL
  const canonicalUrl = `${window.location.origin}/lease/${listingId}`;

  // Extract address components for structured data
  const getAddressComponents = () => {
    if (!listingData.address) return {};

    if (typeof listingData.address === "string") {
      // Try to parse string address
      const parts = listingData.address.split(",").map((s) => s.trim());
      return {
        streetAddress: parts[0] || listingData.address,
        addressLocality: parts[1] || undefined,
        addressRegion: parts[2]?.match(/\b([A-Z]{2})\b/)?.[1] || undefined,
        postalCode: parts[2]?.match(/\d{5}/)?.[0] || undefined,
      };
    }

    // Object address
    return {
      streetAddress:
        listingData.address.street ||
        listingData.address.addressLine1 ||
        listingData.address,
      addressLocality: listingData.address.city,
      addressRegion: listingData.address.state,
      postalCode:
        listingData.address.zipCode ||
        listingData.address.zip ||
        listingData.address.postalCode,
    };
  };

  const addressComponents = getAddressComponents();

  // Determine schema type based on unit type or default to RealEstateListing
  const getSchemaType = (data) => {
    // Check if it's a single family residence
    const unitType = data.unitType || data.propertyType || "";
    const normalizedType = unitType.toLowerCase();

    if (
      normalizedType.includes("single_family") ||
      normalizedType.includes("single-family") ||
      normalizedType.includes("house") ||
      normalizedType === "singlefamily"
    ) {
      return "SingleFamilyResidence";
    }

    // Default to RealEstateListing for apartments, condos, multi-family, etc.
    return "RealEstateListing";
  };

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={fullDescription} />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content="website" />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={`${headline} - ${rentFormatted}`} />
      <meta property="og:description" content={fullDescription} />
      <meta property="og:image" content={coverPhoto} />
      {/^https:\/\//i.test(coverPhoto) && (
        <meta property="og:image:secure_url" content={coverPhoto} />
      )}
      <meta
        property="og:image:alt"
        content={`${headline} - ${listingData.address || "Property listing"}`}
      />
      <meta property="og:site_name" content="LandlordOps" />

      {/* Twitter Card */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={`${headline} - ${rentFormatted}`} />
      <meta name="twitter:description" content={fullDescription} />
      <meta name="twitter:image" content={coverPhoto} />
      <meta
        name="twitter:image:alt"
        content={`${headline} - ${listingData.address || "Property listing"}`}
      />

      {/* Additional SEO */}
      <meta name="robots" content="index, follow" />
      <meta name="author" content="LandlordOps" />

      {/* Structured Data (JSON-LD) */}
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org",
          "@type": getSchemaType(listingData),
          name: listingData.address || headline,
          description: description,
          url: canonicalUrl,
          address: {
            "@type": "PostalAddress",
            ...(addressComponents.streetAddress && {
              streetAddress: addressComponents.streetAddress,
            }),
            ...(addressComponents.addressLocality && {
              addressLocality: addressComponents.addressLocality,
            }),
            ...(addressComponents.addressRegion && {
              addressRegion: addressComponents.addressRegion,
            }),
            ...(addressComponents.postalCode && {
              postalCode: addressComponents.postalCode,
            }),
          },
          ...(listingData.bedrooms && {
            numberOfBedroomsTotal: listingData.bedrooms,
            numberOfRooms: listingData.bedrooms + (listingData.bathrooms || 0),
          }),
          ...(listingData.bathrooms && {
            numberOfBathroomsTotal: listingData.bathrooms,
          }),
          ...(listingData.sqft && {
            floorSize: {
              "@type": "QuantitativeValue",
              value: listingData.sqft,
              // UNECE unit code for square foot
              unitCode: "FTK",
            },
          }),
          image:
            listingData.photos && listingData.photos.length > 0
              ? listingData.photos
              : [coverPhoto],
          ...(Array.isArray(listingData.amenities) &&
          listingData.amenities.length > 0
            ? {
                amenityFeature: listingData.amenities.map((name) => ({
                  "@type": "LocationFeatureSpecification",
                  name,
                  value: true,
                })),
              }
            : {}),
          ...(typeof listingData.rules?.petsAllowed === "boolean"
            ? { petsAllowed: listingData.rules.petsAllowed }
            : {}),
          offers: {
            "@type": "Offer",
            price: rentAmount,
            priceCurrency: "USD",
            availability: "https://schema.org/InStock",
            ...(listingData.availableDate
              ? { availabilityStarts: listingData.availableDate }
              : {}),
          },
        })}
      </script>
    </Helmet>
  );
};

export default SEOHead;
